import { Loader } from "@/components/common/Loader";
import { useEffect, useRef, useCallback } from "react";

interface InfiniteScrollProps {
  children: React.ReactNode;
  loadMore: () => void;
  hasMore: boolean;
  loader?: React.ReactNode;
  threshold?: number;
}

export const InfiniteScroll = ({
  children,
  loadMore,
  hasMore,
  loader = <Loader />,
  threshold = 100,
}: InfiniteScrollProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (!entry.isIntersecting || !hasMore) return;

      loadMore();
    },
    [hasMore, loadMore],
  );

  useEffect(() => {
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: `0px 0px ${threshold}px 0px`,
      threshold: 0,
    });

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [handleIntersection, threshold]);

  return (
    <>
      {children}
      {hasMore && loader}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </>
  );
};
